import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocale } from '@/lib/utils';
import { isNotStartedSession } from '@/lib/analyticsSessionStatus';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Check, HelpCircle, List, MessageSquare, Tag, Users } from 'lucide-react';

const COLORS = {
  OFFER: '#FACC15',
  QUIZ: '#FF6526',
  SURVEY: '#B94746',
  MESSAGES: '#2C6CFF',
  PRESENCE: '#6DFFCD',
};

export const SessionTimelineChart = ({
  data = [],
  interventions = [],
  timezone = 'Europe/Paris',
  status = null,
}: any) => {
  const [activeSeries, setActiveSeries] = useState({
    presence: true,
    messages: true,
    offers: true,
    surveys: true,
    quiz: true,
  });

  const maxY = useMemo(() => {
    if (!data.length) return 20;

    const peakValue = data.reduce((max: number, d: any) => {
      const currentValues = [
        activeSeries.presence ? d.concurrentUsersCount : 0,
        activeSeries.messages ? d.messagesCount : 0,
        activeSeries.offers ? d.offersCount : 0,
        activeSeries.quiz ? d.quizCount : 0,
        activeSeries.surveys ? d.surveysCount : 0,
      ];
      return Math.max(max, ...currentValues);
    }, 0);
    return Math.max(peakValue + 5, 20);
  }, [data, activeSeries]);

  const timelineDataList = useMemo(() => {
    return data.map((point: any) => ({
      ...point,
      displayTime: new Intl.DateTimeFormat(getLocale(), {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: timezone,
      }).format(new Date(point.timestamp.endsWith('Z') ? point.timestamp : point.timestamp + 'Z')),
    }));
  }, [data, timezone]);

  const handleToggle = (key: keyof typeof activeSeries) =>
    setActiveSeries((prev) => ({ ...prev, [key]: !prev[key] }));

  const { t } = useTranslation('analytics');

  if (!data.length)
    return (
      <div className="h-[450px] flex items-center justify-center italic text-[#6d6e7a]">
        {/* Une session jamais lancée n'aura jamais de données : ne pas laisser croire à un chargement. */}
        {isNotStartedSession(status)
          ? t('session.timeline.notStarted')
          : t('session.timeline.waiting')}
      </div>
    );

  return (
    <div className="bg-white p-6 rounded-[20px] border border-[#f3f3f4] flex flex-col gap-8">
      <div className="flex flex-col gap-6">
        <h3 className="text-[20px] font-medium text-[#0c0e21]">{t('session.timeline.title')}</h3>
        <div className="flex flex-wrap gap-3">
          <FilterBtn
            label={t('session.timeline.filters.presence')}
            active={activeSeries.presence}
            onClick={() => handleToggle('presence')}
            color={COLORS.PRESENCE}
            icon={<Users className="w-4 h-4" />}
          />
          <FilterBtn
            label={t('session.timeline.filters.messages')}
            active={activeSeries.messages}
            onClick={() => handleToggle('messages')}
            color={COLORS.MESSAGES}
            icon={<MessageSquare className="w-4 h-4" />}
          />
          <FilterBtn
            label={t('session.timeline.filters.offers')}
            active={activeSeries.offers}
            onClick={() => handleToggle('offers')}
            color={COLORS.OFFER}
            icon={<Tag className="w-4 h-4" />}
          />
          <FilterBtn
            label={t('session.timeline.filters.surveys')}
            active={activeSeries.surveys}
            onClick={() => handleToggle('surveys')}
            color={COLORS.SURVEY}
            icon={<List className="w-4 h-4" />}
          />
          <FilterBtn
            label={t('session.timeline.filters.quiz')}
            active={activeSeries.quiz}
            onClick={() => handleToggle('quiz')}
            color={COLORS.QUIZ}
            icon={<HelpCircle className="w-4 h-4" />}
          />
        </div>
      </div>

      <div className="h-[450px] w-full relative">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={timelineDataList}
            margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
          >
            <XAxis
              dataKey="timestamp"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6d6e7a', fontSize: 12 }}
              dy={10}
              tickFormatter={(val) =>
                timelineDataList.find((d: any) => d.timestamp === val)?.displayTime || val
              }
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6d6e7a', fontSize: 12 }}
              allowDecimals={false}
              domain={[0, maxY]}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#f3f3f4', strokeWidth: 1 }} />
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f3f4" />

            {/* Rendu des zones de durée (Interventions) */}
            {interventions?.map((iv: any, idx: number) => {
              const typeKey = iv.type.replace('_TEMPLATE', '');
              // On ignore les plans clefs visuellement dans cette version
              if (typeKey === 'WEBINAR_SLIDE' || typeKey === 'WEBINAR_RESOURCE') return null;

              const isVisible =
                (typeKey === 'OFFER' && activeSeries.offers) ||
                (typeKey === 'QUIZ' && activeSeries.quiz) ||
                (typeKey === 'SURVEY' && activeSeries.surveys);

              const startPoint = timelineDataList[iv.startMinute];
              const endIdx =
                iv.endMinute === -1 || iv.endMinute >= timelineDataList.length
                  ? timelineDataList.length - 1
                  : iv.endMinute;
              const endPoint = timelineDataList[endIdx];

              if (!isVisible || !startPoint || !endPoint) return null;

              return (
                <ReferenceArea
                  key={`zone-${idx}`}
                  x1={startPoint.timestamp}
                  x2={endPoint.timestamp}
                  y1={0}
                  y2={maxY}
                  ifOverflow="extendDomain"
                  fill={COLORS[typeKey as keyof typeof COLORS] || '#ccc'}
                  fillOpacity={0.12}
                  stroke={COLORS[typeKey as keyof typeof COLORS] || '#ccc'}
                  strokeOpacity={0.2}
                />
              );
            })}

            {activeSeries.presence && (
              <Area
                type="monotone"
                dataKey="concurrentUsersCount"
                stroke={COLORS.PRESENCE}
                strokeWidth={3}
                fillOpacity={0.4}
                fill={COLORS.PRESENCE}
                animationDuration={1000}
              />
            )}
            {activeSeries.messages && (
              <Line
                type="monotone"
                dataKey="messagesCount"
                stroke={COLORS.MESSAGES}
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
              />
            )}
            {activeSeries.offers && (
              <Line
                type="monotone"
                dataKey="offersCount"
                stroke={COLORS.OFFER}
                strokeWidth={2}
                dot={false}
              />
            )}
            {activeSeries.quiz && (
              <Line
                type="monotone"
                dataKey="quizCount"
                stroke={COLORS.QUIZ}
                strokeWidth={2}
                dot={false}
              />
            )}
            {activeSeries.surveys && (
              <Line
                type="monotone"
                dataKey="surveysCount"
                stroke={COLORS.SURVEY}
                strokeWidth={2}
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const FilterBtn = ({ label, active, onClick, color, icon }: any) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-3 px-4 py-2 rounded-[8px] border transition-all ${
      active ? 'bg-white border-primary shadow-sm' : 'bg-white border-[#f3f3f4] opacity-60'
    }`}
  >
    <div
      className={`w-5 h-5 rounded-[4px] flex items-center justify-center border ${active ? 'bg-primary border-primary' : 'bg-white border-[#d1d1d6]'}`}
    >
      {active && <Check className="w-3 h-3 text-white" strokeWidth={4} />}
    </div>
    <div className="flex items-center gap-2">
      <span style={{ color: active ? '#0c0e21' : '#6d6e7a' }} className="text-sm font-medium">
        {label}
      </span>
      <div style={{ color }}>{icon}</div>
    </div>
  </button>
);

const CustomTooltip = ({ active, payload }: any) => {
  const { t } = useTranslation('analytics');
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white p-4 rounded-[12px] border border-[#f3f3f4] shadow-xl min-w-[220px] flex flex-col gap-3">
      <div className="flex flex-col border-b border-[#f3f3f4] pb-2">
        <span className="text-[10px] uppercase font-bold text-[#6d6e7a]">
          {t('session.timeline.tooltip.position')}
        </span>
        <span className="text-sm font-semibold text-[#0c0e21]">{d.displayTime}</span>
      </div>
      <div className="flex flex-col gap-2 pt-1">
        <TooltipRow
          label={t('session.timeline.tooltip.presence')}
          val={d.concurrentUsersCount}
          color={COLORS.PRESENCE}
          icon={<Users className="w-3 h-3" />}
        />
        {d.messagesCount > 0 && (
          <TooltipRow
            label={t('session.timeline.tooltip.messages')}
            val={d.messagesCount}
            color={COLORS.MESSAGES}
            icon={<MessageSquare className="w-3 h-3" />}
          />
        )}
        {d.offersCount > 0 && (
          <TooltipRow
            label={t('session.timeline.tooltip.offerClicks')}
            val={d.offersCount}
            color={COLORS.OFFER}
            icon={<Tag className="w-4 h-4" />}
          />
        )}
        {d.quizCount > 0 && (
          <TooltipRow
            label={t('session.timeline.tooltip.quizAnswers')}
            val={d.quizCount}
            color={COLORS.QUIZ}
            icon={<HelpCircle className="w-4 h-4" />}
          />
        )}
        {d.surveysCount > 0 && (
          <TooltipRow
            label={t('session.timeline.tooltip.surveyAnswers')}
            val={d.surveysCount}
            color={COLORS.SURVEY}
            icon={<List className="w-4 h-4" />}
          />
        )}
      </div>
    </div>
  );
};

const TooltipRow = ({ label, val, color, icon }: any) => (
  <div className="flex justify-between items-center">
    <div className="flex items-center gap-2" style={{ color }}>
      {icon}
      <span className="text-xs text-[#6d6e7a]">{label}</span>
    </div>
    <span className="text-xs font-bold text-[#0c0e21]">{val}</span>
  </div>
);
